import type { FormField } from '@lucid/lego';
import {
  getClosedSubStatusOptions,
  getJunkPrimaryReasons,
  getNotInterestedPrimaryReasons,
  getSecondaryReasonsByPrimary,
} from '../../../constants';

const buildStatusButtons = (
  handleCancel: () => void,
  isLoading?: boolean,
  labels?: Record<string, string>
): FormField => ({
  id: 'buttonGroup',
  fieldType: 'buttonGroup',
  align: 'right',
  buttons: [
    {
      id: 'cancel',
      label: labels?.['form.cancel']?.toUpperCase() ?? 'CANCEL',
      type: 'secondary',
      color: 'neutral',
      size: 'medium',
      onClick: handleCancel,
      disabled: isLoading,
      isFluid: true,
    },
    {
      id: 'save',
      label: isLoading
        ? (labels?.['form.saving'] ?? 'SAVING...')
        : (labels?.['form.save']?.toUpperCase() ?? 'SAVE'),
      type: 'primary',
      color: 'neutral',
      size: 'medium',
      disabled: isLoading,
      isFluid: true,
    },
  ],
});

/**
 * Build confirmation-only fields for reopening a closed prospect.
 */
export const buildOpenFormFields = (
  handleCancel: () => void,
  isLoading?: boolean,
  labels?: Record<string, string>
): FormField[] => [
  {
    id: 'updatingStatusTo',
    fieldType: 'content',
    width: 'full',
    label: labels?.['form.updatingStatusTo'] ?? 'UPDATING STATUS TO',
    content: labels?.['const.PROSPECT_STATUS_STEPS.open'] ?? 'Open',
  },
  buildStatusButtons(handleCancel, isLoading, labels),
];

/**
 * Build form fields for engagement status (Contacting/Engaged)
 */
export const buildEngagementFormFields = (
  updatingToStatus: string,
  initialNextEngagementDate: string,
  handleCancel: () => void,
  isLoading?: boolean,
  labels?: Record<string, string>
): FormField[] => [
  {
    id: 'updatingStatusTo',
    fieldType: 'content',
    width: 'full',
    label: labels?.['form.updatingStatusTo'] ?? 'UPDATING STATUS TO',
    content: updatingToStatus,
  },
  {
    id: 'nextEngagementDate',
    fieldType: 'date_picker',
    name: 'nextEngagementDate',
    apiKey: 'nextEngagementDate',
    label: labels?.['form.nextEngagementDate'] ?? 'Next Engagement Date',
    required: true,
    width: 'full',
    size: 'medium',
    minDate: 'today',
    defaultValue: initialNextEngagementDate,
    validationMessages: {
      required: labels?.['form.nextEngagementDateRequired'] ?? 'Next Engagement Date is required',
    },
  },
  {
    id: 'notes',
    fieldType: 'textarea',
    name: 'notes',
    apiKey: 'notes',
    label: labels?.['form.notes'] ?? 'Notes',
    placeholder: labels?.['form.notesPlaceholder'] ?? 'Enter Notes',
    width: 'full',
    rows: 4,
  },
  buildStatusButtons(handleCancel, isLoading, labels),
];

/**
 * Build form fields for closed status using dependency for conditional rendering
 */
export const buildClosedFormFields = (
  handleCancel: () => void,
  isLoading?: boolean,
  labels?: Record<string, string>
): FormField[] => {
  // Get localized options
  const closedSubStatusOptions = getClosedSubStatusOptions(labels);
  const junkPrimaryReasons = getJunkPrimaryReasons(labels);
  const notInterestedPrimaryReasons = getNotInterestedPrimaryReasons(labels);
  const secondaryReasonsByPrimary = getSecondaryReasonsByPrimary(labels);

  return [
    {
      id: 'updatingStatusTo',
      fieldType: 'content',
      width: 'full',
      label: labels?.['form.updatingStatusTo'] ?? 'UPDATING STATUS TO',
      content: labels?.['details.closed'] ?? 'Closed',
    },
    {
      id: 'subStatus',
      fieldType: 'dropdown',
      name: 'subStatus',
      apiKey: 'subStatus',
      label: labels?.['form.subStatus'] ?? 'Sub Status',
      required: true,
      width: 'full',
      options: closedSubStatusOptions,
      placeholder: labels?.['form.subStatusPlaceholder'] ?? 'Select Sub Status',
      validationMessages: {
        required: labels?.['form.subStatusRequired'] ?? 'Sub Status is required',
      },
    },
    // Primary Reason for Junk (default to Invalid Email since it's the only option)
    {
      id: 'primaryCloseReason_junk',
      fieldType: 'dropdown',
      name: 'primaryCloseReason',
      apiKey: 'primaryCloseReason',
      label: labels?.['form.primaryReason'] ?? 'Primary Reason',
      required: true,
      width: 'full',
      options: junkPrimaryReasons,
      defaultValue: 'invalid_email',
      placeholder: labels?.['form.reasonPlaceholder'] ?? 'Select Reason',
      dependency: {
        name: 'subStatus',
        value: 'junk',
      },
      validationMessages: {
        required: labels?.['form.primaryReasonRequired'] ?? 'Primary Reason is required',
      },
    },
    // Primary Reason for Not Interested
    {
      id: 'primaryCloseReason_not_interested',
      fieldType: 'dropdown',
      name: 'primaryCloseReason',
      apiKey: 'primaryCloseReason',
      label: labels?.['form.primaryReason'] ?? 'Primary Reason',
      required: true,
      width: 'full',
      options: notInterestedPrimaryReasons,
      placeholder: labels?.['form.reasonPlaceholder'] ?? 'Select Reason',
      dependency: {
        name: 'subStatus',
        value: 'not_interested',
      },
      validationMessages: {
        required: labels?.['form.primaryReasonRequired'] ?? 'Primary Reason is required',
      },
    },
    // Secondary Reason fields for each Primary Reason that has secondary options
    // Charging
    {
      id: 'secondaryCloseReason_charging',
      fieldType: 'dropdown',
      name: 'secondaryCloseReason',
      apiKey: 'secondaryCloseReason',
      label: labels?.['form.secondaryReason'] ?? 'Secondary Reason',
      required: true,
      width: 'full',
      options: secondaryReasonsByPrimary.charging,
      placeholder: labels?.['form.reasonPlaceholder'] ?? 'Select Reason',
      dependency: {
        name: 'primaryCloseReason',
        value: 'charging',
      },
      validationMessages: {
        required: labels?.['form.secondaryReasonRequired'] ?? 'Secondary Reason is required',
      },
    },
    // Confidence in Company
    {
      id: 'secondaryCloseReason_confidence_in_company',
      fieldType: 'dropdown',
      name: 'secondaryCloseReason',
      apiKey: 'secondaryCloseReason',
      label: labels?.['form.secondaryReason'] ?? 'Secondary Reason',
      required: true,
      width: 'full',
      options: secondaryReasonsByPrimary.confidence_in_company,
      placeholder: labels?.['form.reasonPlaceholder'] ?? 'Select Reason',
      dependency: {
        name: 'primaryCloseReason',
        value: 'confidence_in_company',
      },
      validationMessages: {
        required: labels?.['form.secondaryReasonRequired'] ?? 'Secondary Reason is required',
      },
    },
    // Customer Experience
    {
      id: 'secondaryCloseReason_customer_experience',
      fieldType: 'dropdown',
      name: 'secondaryCloseReason',
      apiKey: 'secondaryCloseReason',
      label: labels?.['form.secondaryReason'] ?? 'Secondary Reason',
      required: true,
      width: 'full',
      options: secondaryReasonsByPrimary.customer_experience,
      placeholder: labels?.['form.reasonPlaceholder'] ?? 'Select Reason',
      dependency: {
        name: 'primaryCloseReason',
        value: 'customer_experience',
      },
      validationMessages: {
        required: labels?.['form.secondaryReasonRequired'] ?? 'Secondary Reason is required',
      },
    },
    // Environmental
    {
      id: 'secondaryCloseReason_environmental',
      fieldType: 'dropdown',
      name: 'secondaryCloseReason',
      apiKey: 'secondaryCloseReason',
      label: labels?.['form.secondaryReason'] ?? 'Secondary Reason',
      required: true,
      width: 'full',
      options: secondaryReasonsByPrimary.environmental,
      placeholder: labels?.['form.reasonPlaceholder'] ?? 'Select Reason',
      dependency: {
        name: 'primaryCloseReason',
        value: 'environmental',
      },
      validationMessages: {
        required: labels?.['form.secondaryReasonRequired'] ?? 'Secondary Reason is required',
      },
    },
    // Financing
    {
      id: 'secondaryCloseReason_financing',
      fieldType: 'dropdown',
      name: 'secondaryCloseReason',
      apiKey: 'secondaryCloseReason',
      label: labels?.['form.secondaryReason'] ?? 'Secondary Reason',
      required: true,
      width: 'full',
      options: secondaryReasonsByPrimary.financing,
      placeholder: labels?.['form.reasonPlaceholder'] ?? 'Select Reason',
      dependency: {
        name: 'primaryCloseReason',
        value: 'financing',
      },
      validationMessages: {
        required: labels?.['form.secondaryReasonRequired'] ?? 'Secondary Reason is required',
      },
    },
    // Purchased a Different Vehicle
    {
      id: 'secondaryCloseReason_purchased_a_different_vehicle',
      fieldType: 'dropdown',
      name: 'secondaryCloseReason',
      apiKey: 'secondaryCloseReason',
      label: labels?.['form.secondaryReason'] ?? 'Secondary Reason',
      required: true,
      width: 'full',
      options: secondaryReasonsByPrimary.purchased_a_different_vehicle,
      placeholder: labels?.['form.reasonPlaceholder'] ?? 'Select Reason',
      dependency: {
        name: 'primaryCloseReason',
        value: 'purchased_a_different_vehicle',
      },
      validationMessages: {
        required: labels?.['form.secondaryReasonRequired'] ?? 'Secondary Reason is required',
      },
    },
    // Service Network
    {
      id: 'secondaryCloseReason_service_network',
      fieldType: 'dropdown',
      name: 'secondaryCloseReason',
      apiKey: 'secondaryCloseReason',
      label: labels?.['form.secondaryReason'] ?? 'Secondary Reason',
      required: true,
      width: 'full',
      options: secondaryReasonsByPrimary.service_network,
      placeholder: labels?.['form.reasonPlaceholder'] ?? 'Select Reason',
      dependency: {
        name: 'primaryCloseReason',
        value: 'service_network',
      },
      validationMessages: {
        required: labels?.['form.secondaryReasonRequired'] ?? 'Secondary Reason is required',
      },
    },
    // Timing
    {
      id: 'secondaryCloseReason_timing',
      fieldType: 'dropdown',
      name: 'secondaryCloseReason',
      apiKey: 'secondaryCloseReason',
      label: labels?.['form.secondaryReason'] ?? 'Secondary Reason',
      required: true,
      width: 'full',
      options: secondaryReasonsByPrimary.timing,
      placeholder: labels?.['form.reasonPlaceholder'] ?? 'Select Reason',
      dependency: {
        name: 'primaryCloseReason',
        value: 'timing',
      },
      validationMessages: {
        required: labels?.['form.secondaryReasonRequired'] ?? 'Secondary Reason is required',
      },
    },
    // Vehicle Features
    {
      id: 'secondaryCloseReason_vehicle_features',
      fieldType: 'dropdown',
      name: 'secondaryCloseReason',
      apiKey: 'secondaryCloseReason',
      label: labels?.['form.secondaryReason'] ?? 'Secondary Reason',
      required: true,
      width: 'full',
      options: secondaryReasonsByPrimary.vehicle_features,
      placeholder: labels?.['form.reasonPlaceholder'] ?? 'Select Reason',
      dependency: {
        name: 'primaryCloseReason',
        value: 'vehicle_features',
      },
      validationMessages: {
        required: labels?.['form.secondaryReasonRequired'] ?? 'Secondary Reason is required',
      },
    },
    // Note: No secondary reason field for 'invalid_email' as it has no secondary reasons
    buildStatusButtons(handleCancel, isLoading, labels),
  ];
};
